import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Text,
  Textarea,
  TextLabel,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { Steps } from 'antd';
import './index.less';
import { useSiderWidthStore } from '@Models/useSiderWidthModel';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useTranslation } from 'react-i18next';
import { RoleDefinition } from './RoleDefinition';
import { useNavigateWithHandler } from '@Utils/hooks';
import { useLocation, useParams } from 'react-router-dom';
import NavBack from '@Components/Common/NavBack';
import {
  InputChildRef,
  InputWithValid,
} from '@Components/Common/Form/InputWithValid';
import { validateIsNotWhitespace } from '@Utils/validate';
import { ValidProps } from '@Types/index';
import { AdministrationApi } from '@Apis';
import { CreateRoleReqDto, RoleTarget } from '@Types/administrator';
import { useRoleDefinitionStore } from '@Models/rbac/useRoleDefinition';
import CSpinner from '@Components/CSpinner';
import RoleAssigment from './RoleAssigment';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'src/QueryClient';

export const AddRole = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { siderWidth } = useSiderWidthStore();
  const { widthEditablePermission } = usePermissionStore();
  const navigate = useNavigateWithHandler();
  const location = useLocation();
  const { id } = useParams();
  const isAddRoleNoTarget = location.pathname.includes(
    'add-role-without-target'
  );
  // const [isAddRoleNoTarget, setIsAddRoleNoTarget] =
  React.useState<boolean>(false);
  const [checkTargets, setCheckTargets] = React.useState<RoleTarget[]>([]);
  const stepStyle = {
    color: colorStyle.color.secondary,
    backgroundColor: colorStyle.color.primary,
  };

  const [user, setUser] = React.useState({
    name: '',
    description: '',
  });
  const [isEdit, setIsEdit] = React.useState(false);
  const { rolePermission, setRolePermission } = useRoleDefinitionStore();
  const { orgManagementIds, setOrgManagementIds, setErrorMsg } =
    useRoleDefinitionStore();
  const inputNameRef = useRef<InputChildRef>(null);
  const { t } = useTranslation();
  const [roleTargets, setRoleTargets] = React.useState<RoleTarget[] | null>(
    null
  );
  const {
    isPending: isGetRoleByRoleIdPending,
    data: roleDetail,
    isError: isGetRoleByRoleIdError,
  } = useQuery({
    queryKey: ['getRoleByRoleId', id],
    queryFn: async () => {
      if (!id) return null;
      const res = (await AdministrationApi.getRoleByRoleId(id)).data;
      return res;
    },
  });
  if (isGetRoleByRoleIdError) {
    window.showNotification({
      titleMsg: t('common.apiException.internalError'),
      severity: 'error',
    });
  }
  useEffect(() => {
    if (!roleDetail) return;
    setIsEdit(true);
    setUser({
      name: roleDetail.roleName,
      description: roleDetail.roleDescription,
    });
    setRoleTargets(roleDetail.roleTargets || []);
    if (roleDetail.permissions) {
      console.log('role', roleDetail.orgIds, roleDetail);
      setOrgManagementIds(roleDetail.orgIds);
      setRolePermission(roleDetail.permissions);
    }
  }, [roleDetail, setOrgManagementIds, setRolePermission]);
  const validateName: ValidProps[] = [
    {
      validMethod: validateIsNotWhitespace,
      errMsg: t('reportCommon.tips.errors.requiredField'),
    },
  ];
  const goBack = function () {
    if (isAddRoleNoTarget) {
      navigate(-1);
      return;
    }
    const path = `/administrator/user-management`;
    navigate(path, { replace: true });
  };

  const checkAll = () => {
    inputNameRef.current?.validate(user.name);
    if (inputNameRef.current?.errorMsg !== undefined) return false;

    if (orgManagementIds.length === 0) {
      setErrorMsg(t('administration.rbac.addRole.orgRequired'));
      return false;
    }
    return true;
  };

  const createUserRoleMutation = useMutation({
    mutationFn: async () => {
      const roleInfo: CreateRoleReqDto = {
        roleName: user.name,
        orgIds: orgManagementIds,
        roleDescription: user.description,
        permissions: rolePermission,
        roleTargets: checkTargets || [],
        language: localStorage.getItem('uic_language') || 'en',
      };
      if (isEdit && id) {
        await AdministrationApi.updateRoles(id, roleInfo);
      } else {
        //create role
        await AdministrationApi.createRoles(roleInfo);
      }
    },
    onSuccess: () => {
      if (isEdit) {
        window.showNotification({
          titleMsg: t('administration.rbac.updateUser.successMsg'),
          severity: 'success',
        });
        queryClient
          .invalidateQueries({
            queryKey: ['getLocalAccounts', 'getRoleByRoleId'],
          })
          .then(() => {
            goBack();
          });
      } else {
        window.showNotification({
          titleMsg: t('administration.rbac.addRole.successMsg'),
          severity: 'success',
        });
        queryClient
          .invalidateQueries({ queryKey: ['getLocalAccounts', 'getRoles'] })
          .then(() => {
            goBack();
          });
      }
    },
    onError: (error: any) => {
      if (
        error?.data.error.details[0].reason === 'ROLE;ROLE_NAME_ALREADY_EXISTS'
      ) {
        window.showNotification({
          titleMsg: t('administration.rbac.addRole.existMsg'),
          severity: 'error',
        });
      } else if (error?.data.error.details[0].reason === 'Role;NumExceed') {
        window.showNotification({
          titleMsg: t('administration.rbac.addRole.outOfLimitMsg'),
          severity: 'error',
        });
      }
    },
  });

  const onSave = async () => {
    console.log('save');
    if (!checkAll()) return;
    console.log('save', {
      roleDefinition: {
        rolePermission,
        orgIds: orgManagementIds,
        roleTarget: checkTargets,
      },
    });
    createUserRoleMutation.mutate();
  };
  return (
    <Box>
      {(isGetRoleByRoleIdPending || createUserRoleMutation.isPending) && (
        <CSpinner />
      )}
      <Box>
        <NavBack
          fixed={false}
          onBack={goBack}
          style={{ padding: '12px 16px 12px 16px' }}
        />
      </Box>
      <Box id="addRoleSteps" p="0px 16px">
        <Flex flexDirection="column">
          <Box mb={12}>
            <Divider />
          </Box>
          <Box>
            <Text>{t('administration.rbac.addRole.desc')}</Text>
          </Box>
          <Box mt="32px">
            <Grid templateColumns="min-content auto" columnGap="3x" rowGap="3x">
              <Grid>
                <TextLabel
                  whiteSpace="nowrap"
                  width="128px"
                  className="required"
                >
                  {t('common.label.enterName')}:
                </TextLabel>
              </Grid>
              <Grid>
                <InputWithValid
                  ref={inputNameRef}
                  onChange={(value: string) => {
                    setUser({ ...user, name: value });
                  }}
                  width="572px"
                  value={user.name}
                  validates={validateName}
                  maxLength={255}
                />
              </Grid>
              <Grid>
                <TextLabel whiteSpace="nowrap" width="128px">
                  {t('common.label.description')}:
                </TextLabel>
              </Grid>
              <Grid>
                <Textarea
                  width="572px"
                  height="78px"
                  maxLength={255}
                  value={user.description}
                  onChange={(e: any) => {
                    setUser({ ...user, description: e.target.value });
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box m="22px 0px">
            <Divider />
          </Box>
          {!isAddRoleNoTarget ? (
            <Box m="15px 0px">
              <Steps
                style={stepStyle}
                direction="vertical"
                items={[
                  {
                    title: t('administration.rbac.label.roleDefinition'),
                    status: 'process',
                    description: (
                      <Box>
                        <RoleDefinition />
                      </Box>
                    ),
                  },
                  {
                    title: t('administration.rbac.label.assigment'),
                    status: 'process',
                    description: (
                      <Box>
                        <RoleAssigment
                          isEdit={isEdit}
                          setCheckTargets={setCheckTargets}
                          roleTargets={roleTargets}
                        />
                      </Box>
                    ),
                  },
                ]}
              />
            </Box>
          ) : (
            <Box>
              <RoleDefinition />
            </Box>
          )}
        </Flex>
        <Flex
          position="fixed"
          pl="5x"
          bottom={0}
          left={270 + siderWidth}
          right={0}
          background={colorStyle.background.secondary}
          height={64}
          align="center"
          justify="flex-start"
        >
          <Button
            variant="primary"
            onClick={onSave}
            disabled={
              !widthEditablePermission(PermissionNameEnum.administrator)
            }
          >
            {t('common.button.save')}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};
